// src/components/sections/Skills.js
import React from 'react';
import { Server, Laptop, Cloud, Database } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { skillsData } from '../../data/skills';
import { motion } from 'framer-motion';

/* Commented out SkillBar component
const SkillBar = ({ name, percentage, color }) => {
  const { darkMode } = useTheme();
  
  // Utiliser une approche directe avec style inline pour la largeur
  const getBarColor = () => {
    switch (color) {
      case 'blue': return darkMode ? 'bg-blue-400' : 'bg-blue-600';
      case 'green': return darkMode ? 'bg-green-400' : 'bg-green-600';
      case 'cyan': return darkMode ? 'bg-cyan-400' : 'bg-cyan-600';
      case 'orange': return darkMode ? 'bg-orange-400' : 'bg-orange-600';
      case 'red': return darkMode ? 'bg-red-400' : 'bg-red-600';
      case 'purple': return darkMode ? 'bg-purple-400' : 'bg-purple-600';
      default: return darkMode ? 'bg-blue-400' : 'bg-blue-600';
    }
  };
  
  const getBgColor = () => {
    return darkMode ? 'bg-gray-600' : 'bg-gray-200';
  };
  
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-1">
        <span className="font-medium">{name}</span>
        <span>{percentage}%</span>
      </div>
      <div className={`w-full h-2 rounded-full ${getBgColor()}`}>
        <motion.div 
          className={`h-full rounded-full ${getBarColor()}`}
          initial={{ width: 0 }}
          whileInView={{ width: `${percentage}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </div>
    </div>
  );
};
*/

const Skills = () => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const t = translations[language];
  
  const getIcon = (iconName) => {
    switch (iconName) {
      case 'Server': return <Server className="h-8 w-8" />;
      case 'Laptop': return <Laptop className="h-8 w-8" />;
      case 'Cloud': return <Cloud className="h-8 w-8" />;
      case 'Database': return <Database className="h-8 w-8" />;
      default: return <Server className="h-8 w-8" />;
    }
  };
  
  const getTextColorClass = (color) => {
    switch (color) {
      case 'blue': return darkMode ? 'text-blue-400' : 'text-blue-600';
      case 'green': return darkMode ? 'text-green-400' : 'text-green-600';
      case 'orange': return darkMode ? 'text-orange-400' : 'text-orange-600';
      default: return darkMode ? 'text-blue-400' : 'text-blue-600';
    }
  };
  
  const getBulletColor = (color) => {
    switch (color) {
      case 'blue': return darkMode ? 'bg-blue-400' : 'bg-blue-600';
      case 'green': return darkMode ? 'bg-green-400' : 'bg-green-600';
      case 'orange': return darkMode ? 'bg-orange-400' : 'bg-orange-600';
      default: return darkMode ? 'bg-blue-400' : 'bg-blue-600';
    }
  };
  
  const categoryVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.5
      }
    })
  };
  
  // Get category name based on language
  const getCategoryName = (category) => {
    if (category === "Backend") return t["skills.backend"];
    if (category === "Frontend") return t["skills.frontend"];
    if (category === "DevOps") return t["skills.devops"];
    return category;
  };
  
  return (
    <section id="competences" className={`py-16 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
      <div className="container mx-auto px-6">
        <motion.h2 
          className="text-3xl font-bold mb-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t["skills.title"]}
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {skillsData.categories.map((category, index) => (
            <motion.div
              key={category.name}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={categoryVariants}
              className={`p-6 rounded-lg shadow-lg transition-transform duration-300 transform hover:-translate-y-2 ${
                darkMode ? 'bg-gray-700' : 'bg-white'
              }`}
            >
              <div className="flex items-center mb-4">
                <div className={`mr-3 ${getTextColorClass(category.color)}`}>
                  {getIcon(category.icon)}
                </div>
                <h3 className="text-xl font-semibold">{getCategoryName(category.name)}</h3>
              </div>
              <ul className="space-y-2">
                {category.skills.map((skill) => (
                  <li key={skill} className="flex items-center">
                    <span className={`w-2 h-2 rounded-full ${getBulletColor(category.color)} mr-2`}></span>
                    {skill}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        
        {/* Commented out Skill Bars section
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            {skillsData.skillBars.slice(0, 3).map((skill) => (
              <SkillBar
                key={skill.name}
                name={skill.name}
                percentage={skill.percentage}
                color={skill.color}
              />
            ))}
          </div>
          
          <div className="space-y-6">
            {skillsData.skillBars.slice(3, 6).map((skill) => (
              <SkillBar
                key={skill.name}
                name={skill.name}
                percentage={skill.percentage}
                color={skill.color}
              />
            ))}
          </div>
        </div>
        */}
      </div>
    </section>
  );
};

export default Skills;